/**
 * Grid Aggregate Utility
 *
 * Utilities for managing aggregate toggle state with localStorage persistence.
 * Used for grids that can show aggregated (e.g., only approved) or all versions of records.
 */

/**
 * Generate storage key for a collection's aggregate state
 * @param {string} collection - Collection name (e.g., 'employees')
 * @returns {string} Storage key
 */
export const getAggregateStorageKey = (collection) => {
  if (!collection || typeof collection !== 'string') {
    throw new Error('Collection name must be a non-empty string');
  }
  return `${collection}.showAllVersions`;
};

/**
 * Get aggregate state from localStorage
 * @param {string} collection - Collection name
 * @returns {boolean} Whether to show all versions (false = aggregate/approved only)
 */
export const getStoredAggregateState = (collection) => {
  try {
    const key = getAggregateStorageKey(collection);
    const stored = localStorage.getItem(key);
    return stored === 'true';
  } catch (error) {
    return false; // Default to showing aggregated/approved only
  }
};

/**
 * Save aggregate state to localStorage
 * @param {string} collection - Collection name
 * @param {boolean} showAllVersions - Whether to show all versions
 */
export const saveAggregateState = (collection, showAllVersions) => {
  try {
    const key = getAggregateStorageKey(collection);
    localStorage.setItem(key, String(showAllVersions));
  } catch (error) {
    // Silently fail
  }
};

/**
 * Clear aggregate state from localStorage
 * @param {string} collection - Collection name
 */
export const clearAggregateState = (collection) => {
  try {
    const key = getAggregateStorageKey(collection);
    localStorage.removeItem(key);
  } catch (error) {
    // Silently fail
  }
};

/**
 * Convert showAllVersions state to aggregate parameter
 * @param {boolean} showAllVersions - Whether to show all versions
 * @returns {boolean} Aggregate parameter value (true = only approved, false = all versions)
 */
export const getAggregateValue = (showAllVersions) => {
  return !showAllVersions;
};

/**
 * Get button color based on selection state
 * @param {boolean} showAllVersions - Whether all versions are shown
 * @returns {string} Button color ('primary' or 'lightgray')
 */
export const getAggregateButtonColor = (showAllVersions) => {
  return showAllVersions ? 'primary' : 'lightgray';
};

/**
 * Get button icon based on selection state
 * @param {boolean} showAllVersions - Whether all versions are shown
 * @returns {string} Icon name
 */
export const getAggregateButtonIcon = (showAllVersions) => {
  return showAllVersions ? 'CheckMark' : 'StatusCircleCheckmark';
};

/**
 * Create aggregate toggle handler
 * @param {string} collection - Collection name
 * @param {Function} setState - State setter function
 * @returns {Function} Toggle handler
 */
export const createAggregateToggle = (collection, setState) => {
  return () => {
    setState(prev => {
      const newValue = !prev;
      saveAggregateState(collection, newValue);
      return newValue;
    });
  };
};
